import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ChevronRight, Search } from 'lucide-react';
import { env } from '@config/env';
import { ActionButton } from '@shared/ui/forms/FormFields';
import { Banner } from '@shared/ui/feedback/Banner';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { BrandLoader } from '@shared/ui/feedback/BrandLoader';
import { BottomSheet } from '@shared/ui/containers/BottomSheet';
import { useDebounce } from '@shared/hooks/useDebounce';
import { useBrandingStore } from '@shared/stores/brandingStore';
import { useBrandImagery } from '@shared/hooks/useBrandImagery';
import { resolveDomain } from '@shared/api/domainApi';
import { getEffectiveRootTenantId } from '@shared/lib/ssoRootScope';
import { toApiError } from '@shared/api/errors';
import { searchTenantsByRoot, type TenantSearchResult } from '@features/join/api';

/** Minimum characters before the community search reveals any results. */
const SEARCH_MIN = 3;

/**
 * Welcome / landing screen — the first thing a resident sees. A full-bleed
 * estate photograph (resolved from the tenant's domain config), editorial
 * copy and the two entry CTAs.
 *
 * On an SSO (root-scoped) build, "Create resident account" opens the
 * community picker here — once a community is chosen the resident is routed
 * to `/signup?tenant=<id>` with that community already resolved.
 */
export function Welcome(): JSX.Element {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const branding = useBrandingStore((s) => s.branding);
  const { heroImage } = useBrandImagery();
  const rootTenantId = getEffectiveRootTenantId(env.rootTenantId);
  const name = branding?.name ?? 'IP4Estates';

  // Community picker — opens here on SSO builds and routes to /signup.
  // Auto-opens when the resident is redirected back from /signup.
  const [sheetOpen, setSheetOpen] = useState(params.get('signup') === '1');
  const [pickError, setPickError] = useState<string | null>(null);

  const eyebrow = env.welcomeContent?.eyebrow ?? 'Resident companion';
  const title = env.welcomeContent?.title ?? 'The estate,\nin your pocket.';
  const subtitle =
    env.welcomeContent?.subtitle ??
    'Access, hospitality and your home — from the first tee to the last light.';

  // Resolve the chosen community (branding + domain config) before handing
  // off to the sign-up form, then route with the tenant id as a query param.
  const pickTenant = useMutation({
    mutationFn: async (tenant: TenantSearchResult) => {
      const domain = tenant.resolution_domain;
      if (!domain) throw new Error('This community has no portal domain configured.');
      try {
        localStorage.setItem('paas_active_portal_domain', domain);
      } catch {
        /* storage unavailable — resolution still works for this session */
      }
      await resolveDomain(domain);
      return { tenant };
    },
    onSuccess: ({ tenant }) => {
      setPickError(null);
      setSheetOpen(false);
      navigate(`/signup?tenant=${encodeURIComponent(tenant.tenant_id)}`);
    },
    onError: (e) => setPickError(toApiError(e).message),
  });

  const onCreateAccount = () => {
    setPickError(null);
    if (rootTenantId) setSheetOpen(true);
    else navigate('/signup');
  };

  if (pickTenant.isPending) return <BrandLoader />;

  return (
    <div className="relative flex min-h-full flex-col overflow-hidden bg-primary-900">
      {/* Domain-resolved estate photograph */}
      {heroImage ? (
        <div
          aria-hidden
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url("${heroImage}")` }}
        />
      ) : null}
      <div
        aria-hidden
        className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/40 to-black/85"
      />

      {/* Editorial copy + CTAs. Single padding-bottom: a 2rem floor that grows to
          the home-indicator inset in standalone PWA (was `pb-safe-or-6 pb-8` —
          two conflicting padding-bottom utilities). */}
      <div className="relative z-10 mt-auto flex flex-col gap-6 px-7 pb-[max(2rem,env(safe-area-inset-bottom))]">
        <div>
          <span className="text-[0.6875rem] font-semibold uppercase tracking-[0.18em] text-brass">
            {eyebrow}
          </span>
          <h1 className="display-serif mt-3 whitespace-pre-line text-[2.6rem] leading-[1.05] text-white">
            {title}
          </h1>
          <p className="mt-3 max-w-sm text-body text-white/75">{subtitle}</p>
        </div>

        <div className="flex flex-col gap-3">
          <ActionButton variant="gold" onClick={() => navigate('/login')}>
            Sign in to your home
          </ActionButton>
          <ActionButton onClick={onCreateAccount}>
            Create resident account
          </ActionButton>
        </div>

        <p className="text-center text-caption text-white/45">
          v{env.version} · {name}
        </p>
      </div>

      {rootTenantId ? (
        <CommunitySheet
          open={sheetOpen}
          onOpenChange={setSheetOpen}
          rootTenantId={rootTenantId}
          error={pickError}
          onPick={(tenant) => pickTenant.mutate(tenant)}
        />
      ) : null}
    </div>
  );
}

/* ── Community picker sheet ──────────────────────────────────────────── */

function CommunitySheet({
  open,
  onOpenChange,
  rootTenantId,
  error,
  onPick,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rootTenantId: string;
  error: string | null;
  onPick: (tenant: TenantSearchResult) => void;
}): JSX.Element {
  const [search, setSearch] = useState('');
  const debounced = useDebounce(search.trim(), 300);
  const ready = debounced.length >= SEARCH_MIN;

  const query = useQuery({
    queryKey: ['tenant-search', rootTenantId, debounced],
    queryFn: () => searchTenantsByRoot(rootTenantId, debounced),
    enabled: open && ready,
  });
  const tenants = query.data ?? [];

  return (
    <BottomSheet
      open={open}
      onOpenChange={onOpenChange}
      title={env.communitySelectContent?.title ?? 'Choose your community'}
      description={
        env.communitySelectContent?.subtitle ??
        'Search for the estate or complex you live in to begin registration.'
      }
      height="82dvh"
    >
      <div className="flex flex-col gap-3 pb-safe-or-6">
        <label className="flex items-center gap-2 rounded-pill border border-hairline-strong bg-card px-4 py-3">
          <Search className="h-4 w-4 text-ink-muted" strokeWidth={1.75} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search communities…"
            autoFocus
            className="w-full bg-transparent text-body text-ink placeholder:text-ink-muted focus:outline-none"
          />
        </label>

        {error ? <Banner variant="danger" title={error} className="rounded-md" /> : null}

        {!ready ? (
          <p className="rounded-lg bg-sunken px-4 py-8 text-center text-body-sm text-ink-secondary">
            Type at least {SEARCH_MIN} characters to search for your community.
          </p>
        ) : query.isLoading ? (
          <>
            <Skeleton className="h-16" />
            <Skeleton className="h-16" />
            <Skeleton className="h-16" />
          </>
        ) : tenants.length === 0 ? (
          <p className="rounded-lg bg-sunken px-4 py-8 text-center text-body-sm text-ink-secondary">
            No communities match "{debounced}".
          </p>
        ) : (
          tenants.map((tenant) => {
            const logo = tenant.logo_small_url || tenant.logo_large_url;
            return (
              <button
                key={tenant.tenant_id}
                type="button"
                onClick={() => onPick(tenant)}
                className="flex items-center gap-3 rounded-2xl border border-hairline-strong bg-card p-3 text-left transition-colors hover:bg-sunken"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-md bg-primary-50">
                  {logo ? (
                    <img src={logo} alt="" className="h-full w-full object-contain" />
                  ) : (
                    <span className="text-headline font-semibold text-primary-700">
                      {tenant.name.charAt(0).toUpperCase()}
                    </span>
                  )}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-body font-medium text-ink">
                    {tenant.name}
                  </span>
                  {tenant.resolution_domain ? (
                    <span className="block truncate text-caption text-ink-muted">
                      {tenant.resolution_domain}
                    </span>
                  ) : null}
                </span>
                <ChevronRight className="h-5 w-5 shrink-0 text-ink-muted" strokeWidth={1.75} />
              </button>
            );
          })
        )}
      </div>
    </BottomSheet>
  );
}
